import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { unitAPI, userAPI, financialAPI, advancedAmountAPI } from '../../services/api';
import { useSettings } from '../../contexts/SettingsContext';
import { toast } from 'react-hot-toast';

const UnitDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // For advance amounts
  const [advanceAmounts, setAdvanceAmounts] = useState([]);
  const [advanceAmountsLoading, setAdvanceAmountsLoading] = useState(false);
  const [advanceAmountsError, setAdvanceAmountsError] = useState(null);

  // Months array for built month display
  const months = [
    { id: 1, name: 'January' }, { id: 2, name: 'February' }, { id: 3, name: 'March' },
    { id: 4, name: 'April' }, { id: 5, name: 'May' }, { id: 6, name: 'June' },
    { id: 7, name: 'July' }, { id: 8, name: 'August' }, { id: 9, name: 'September' },
    { id: 10, name: 'October' }, { id: 11, name: 'November' }, { id: 12, name: 'December' }
  ];

  // Helper functions
  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };

  const normalizeStatus = (status) => {
    if (!status) return 'vacant';
    return status.toLowerCase().trim();
  };

  const getStatusBadgeClass = (status) => {
    const normalizedStatus = normalizeStatus(status);
    switch (normalizedStatus) {
      case 'occupied':
        return 'bg-success';
      case 'vacant':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  function formatDateIST(dateString) {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      const options = {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        timeZone: 'Asia/Kolkata'
      };
      
      return date.toLocaleDateString('en-GB', options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  }

  // Fetch unit data
  const fetchUnitData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch owners data for enriching unit information
      const ownersResponse = await userAPI.getAllUsers({ role: 'owner' });
      const ownersData = ownersResponse.data || [];
      
      // Fetch unit details
      const response = await unitAPI.getUnitById(id);
      
      if (response.data) {
        const freshUnit = {
          ...response.data,
          status: normalizeStatus(response.data.status)
        };
        
        // Merge owner details for contact info
        const owner = ownersData.find(o => o.id === freshUnit.owner_id);
        const enrichedUnit = {
          ...freshUnit,
          owner_name: owner ? owner.name : freshUnit.owner_name,
          contact_email: owner ? owner.email : freshUnit.contact_email,
          contact_phone: owner && owner.phone ? owner.phone : freshUnit.contact_phone
        };
        
        // Fetch transaction information for this unit
        try {
          const financialsResponse = await financialAPI.getAllFinancials();
          const allTransactions = financialsResponse.data || [];
          
          const parseAmount = (value) => parseFloat((value || '0').toString().replace(/,/g, ''));

          // Filter transactions for this specific unit
          const unitTransactions = allTransactions.filter(transaction => 
            transaction.unit_id === parseInt(id)
          ).sort((a, b) => new Date(b.date) - new Date(a.date));
          
          // Calculate summary information
          const paidTransactions = unitTransactions.filter(
            t => t.type === 'payment' && (t.status === 'paid' || t.status === 'partially_paid')
          );
          const totalPaid = unitTransactions.reduce((sum, t) => {
            if (t.type === 'payment') {
              if (t.status === 'paid') {
                return sum + parseAmount(t.amount);
              } else if (t.status === 'partially_paid') {
                return sum + parseAmount(t.amount);
              }
            }
            return sum;
          }, 0);
          
          // Get last payment date
          const lastPayment = paidTransactions.length > 0 ? paidTransactions[0] : null;
          
          // Calculate pending amount from transactions with pending status
          const pendingTransactions = unitTransactions.filter(
            t => t.type === 'payment' && (t.status === 'pending' || t.status === 'partially_paid')
          );

          const pendingAmount = pendingTransactions.reduce((sum, t) => {
            if (t.status === 'pending') {
              return sum + parseAmount(t.amount);
            } else if (t.status === 'partially_paid') {
              return sum + parseAmount(t.remaining_amount);
            }
            return sum;
          }, 0);
          
          // Calculate months unpaid based on pending transactions
          const monthsUnpaid = pendingTransactions.length;
          
          enrichedUnit.transactionInfo = {
            transactions: unitTransactions,
            totalPaid: totalPaid,
            lastPaymentDate: lastPayment ? lastPayment.date : null,
            pendingAmount: pendingAmount,
            monthsUnpaid: monthsUnpaid
          };
          
        } catch (transactionErr) {
          console.error('Error fetching transaction data:', transactionErr);
          enrichedUnit.transactionInfo = {
            transactions: [],
            totalPaid: 0,
            lastPaymentDate: null,
            pendingAmount: 0,
            monthsUnpaid: 0
          };
        }
        
        setUnit(enrichedUnit);
        
        // Fetch advance amounts for this unit
        await fetchAdvanceAmounts(id);
      } else {
        setError('Unit not found. Please check the unit ID and try again.');
        toast.error('Unit not found');
      }
    } catch (err) {
      console.error('Error fetching unit data:', err);
      if (err.response?.status === 404) {
        setError('Unit not found. Please check the unit ID and try again.');
        toast.error('Unit not found');
      } else if (err.response?.status === 403) {
        setError('Access denied. You do not have permission to view this unit.');
        toast.error('Access denied');
      } else {
        setError(`Failed to load unit details: ${err.response?.data?.message || err.message || 'Please try again.'}`);
        toast.error('Failed to load unit details');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch advance amounts for a unit
  const fetchAdvanceAmounts = async (unitId) => {
    if (!unitId) return;
    
    try {
      setAdvanceAmountsLoading(true);
      setAdvanceAmountsError(null);
      
      const response = await advancedAmountAPI.getByUnitId(unitId);
      
      if (response.data && response.data.success) {
        setAdvanceAmounts(response.data.data || []);
      } else {
        setAdvanceAmountsError('Failed to fetch advance amounts');
      }
    } catch (err) {
      setAdvanceAmountsError('Unable to fetch advance amounts. Please try again later.');
      console.error('Error fetching advance amounts:', err);
    } finally {
      setAdvanceAmountsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchUnitData();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="container-fluid mt-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mt-2">Loading unit details...</p>
        </div>
      </div>
    );
  }

  if (error || !unit) {
    return (
      <div className="container-fluid mt-4">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error || 'Unit not found'}
        </div>
        <button 
          className="btn btn-secondary" 
          onClick={() => navigate('/admin/units')}
        >
          <i className="fas fa-arrow-left me-2"></i>
          Back to Units
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="page-title">
            <i className="fas fa-eye me-2"></i>
            {unit.unit_type || 'Unit'} #{unit.villa_number || unit.unit_number} Details
          </h1>
          
        </div>
        <div className="d-flex gap-2"> 
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/admin/units')}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Units
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          {/* Basic Unit Information */}
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h6 className="mb-0"><i className="fas fa-home me-2"></i>Basic Information</h6>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <label className="fw-bold w-50">Unit ID:</label>
                    <p className="form-control-plaintext w-50">#{unit.id}</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <label className="fw-bold w-50">Date Created:</label>
                    <p className="form-control-plaintext w-50">
                      {unit.created_at ? new Date(unit.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <label className="fw-bold w-50">Unit Number:</label>
                    <p className="form-control-plaintext w-50">{unit.villa_number || unit.unit_number || 'N/A'}</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <label className="fw-bold w-50">Unit Type:</label>
                    <p className="form-control-plaintext w-50">
                      <span className="badge bg-secondary">{unit.unit_type || 'N/A'}</span>
                    </p>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <label className="fw-bold w-50">Status:</label>
                    <p className="form-control-plaintext w-50">
                      <span className={`badge ${getStatusBadgeClass(unit.status)} fs-6`}>
                        {unit.status === 'occupied' ? 'Occupied' : 'Vacant'}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <label className="fw-bold w-50">Size:</label>
                    <p className="form-control-plaintext w-50">{unit.size || 'Not specified'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
           
          {/* Property Details */}
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h6 className="mb-0"><i className="fas fa-building me-2"></i>Property Details</h6>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="d-flex align-items-center mb-3">
                    <label className="fw-bold w-50">Bedrooms:</label>
                    <p className="form-control-plaintext w-50">{unit.bedrooms || 'Not specified'}</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-center mb-3">
                    <label className="fw-bold w-50">Bathrooms:</label>
                    <p className="form-control-plaintext w-50">{unit.bathrooms || 'Not specified'}</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex align-items-center mb-3">
                    <label className="fw-bold w-50">Built Year:</label>
                    <p className="form-control-plaintext w-50">{unit.built_year || 'Not specified'}</p>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <label className="fw-bold w-50">Built Month:</label>
                    <p className="form-control-plaintext w-50">
                      {unit.built_month_id ? 
                        months.find(m => m.id === parseInt(unit.built_month_id))?.name || 'Not specified' 
                        : 'Not specified'
                      }
                    </p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <label className="fw-bold w-50">Transaction Start Date:</label>
                    <p className="form-control-plaintext w-50">
                      {unit.transaction_start_date ? 
                        new Date(unit.transaction_start_date).toLocaleDateString() 
                        : 'Not specified'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Owner Information */}
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h6 className="mb-0"><i className="fas fa-user me-2"></i>Owner Information</h6>
            </div>
            <div className="card-body">
              {unit.owner_id ? (
                <div className="row">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center mb-3">
                      <label className="fw-bold w-50">Owner Name:</label>
                      <p className="form-control-plaintext w-50">{unit.owner_name || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-center mb-3">
                      <label className="fw-bold w-50">Email:</label>
                      <p className="form-control-plaintext w-50">
                        {unit.contact_email ? (
                          <a href={`mailto:${unit.contact_email}`} className="text-decoration-none">
                            {unit.contact_email}
                          </a>
                        ) : 'N/A'}
                      </p>
                    </div>
                  </div>
                  {unit.contact_phone && (
                    <div className="col-md-6">
                      <div className="d-flex align-items-center mb-3">
                        <label className="fw-bold w-50">Phone:</label>
                        <p className="form-control-plaintext w-50">
                          <a href={`tel:${unit.contact_phone}`} className="text-decoration-none">
                            {unit.contact_phone}
                          </a>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  No owner assigned to this unit
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Financial Information - Full Width */}
      <div className="row">
        <div className="col-12">
          <div className="card mb-4">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <h6 className="finance-card-header d-flex justify-content-between mb-0">
               <span>Financial Information & Transactions</span>
               <label><b>Unit #{unit.villa_number}</b></label>
               <label><b>{unit.owner_name}</b></label>
               <small className="text-muted">
                {unit.transactionInfo && unit.transactionInfo.transactions ? 
                  `${unit.transactionInfo.transactions.length} transaction${unit.transactionInfo.transactions.length !== 1 ? 's' : ''}` : 
                  '0 transactions'
                }
               </small>
              </h6>
            </div>
            <div className="card-body">
              {/* Summary Information */}
              <div className="row mb-4">
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded">
                    <div className="fw-bold text-primary">Monthly Fee</div>
                    <div className="fs-6">
                      {unit.maintenance_fee ? 
                        <span className="text-success fw-bold">{formatCurrency(unit.maintenance_fee)}</span> : 
                        <span className="text-muted">Not set</span>
                      }
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded">
                    <div className="fw-bold text-success">Total Paid</div>
                    <div className="fs-6 text-success fw-bold">
                      {formatCurrency(unit.transactionInfo?.totalPaid || 0)}
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded">
                    <div className="fw-bold text-danger">Pending</div>
                    <div className="fs-6 text-danger fw-bold">
                      {formatCurrency(unit.transactionInfo?.pendingAmount || 0)}
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded">
                    <div className="fw-bold">Status</div>
                    <div className="fs-6">
                      {unit.transactionInfo && unit.transactionInfo.monthsUnpaid > 0 ? (
                        <span className="badge bg-danger">
                          {unit.transactionInfo.monthsUnpaid} Month{unit.transactionInfo.monthsUnpaid > 1 ? 's' : ''} Due
                        </span>
                      ) : (
                        <span className="badge bg-success">Up to Date</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Transactions Table */}
              {unit.transactionInfo && unit.transactionInfo.transactions && unit.transactionInfo.transactions.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm table-striped">
                    <thead className="table-dark">
                      <tr>
                        <th>Last Updated</th> 
                        <th>Category</th>
                        <th>Amount</th>
                        <th>Remaining Amount</th>
                        <th>Status</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unit.transactionInfo.transactions.slice(0, 20).map((transaction, index) => (
                        <tr key={transaction.id || index}>
                          <td className="text-nowrap">
                            {formatDateIST(transaction.updated_at)}
                          </td>
                          <td className="text-capitalize">
                            {transaction.category ? transaction.category.replace('_', ' ') : 'N/A'}
                          </td>
                          <td className="fw-bold">
                            <span className={transaction.type === 'payment' ? 'text-success' : 'text-danger'}>
                              {formatCurrency(transaction.amount || 0)}
                            </span>
                          </td>
                          <td className="fw-bold">
                            <span className={transaction.remaining_amount > 0 ? 'text-danger' : 'text-secondary'}>
                              {formatCurrency(transaction.remaining_amount || 0)}
                            </span>
                          </td>
                          <td>
                            <span className={`badge rounded-pill ${
                              transaction.status === 'paid' ? 'bg-success' :
                              transaction.status === 'pending' ? 'bg-warning text-dark' :
                              transaction.status === 'hold' ? 'bg-secondary' :
                              transaction.status === 'partially_paid' ? 'bg-info' : 'bg-light text-dark'
                            }`}>
                              {transaction.status ? transaction.status.replace('_', ' ').toUpperCase() : 'PAID'}
                            </span>
                          </td>
                          <td className="text-muted">
                            {transaction.remarks || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {unit.transactionInfo.transactions.length > 20 && (
                    <div className="text-center mt-2">
                      <small className="text-muted">
                        Showing recent 20 transactions out of {unit.transactionInfo.transactions.length} total
                      </small>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="fas fa-receipt fa-2x mb-2 d-block"></i>
                  <p>No transactions found for this unit</p>
                  <small>Financial transactions will appear here once payments or expenses are recorded.</small>
                </div>
              )}
              
              {/* Warning Alert for Pending Payments */}
              {unit.transactionInfo && unit.transactionInfo.pendingAmount > 0 && (
                <div className="alert alert-warning mt-3">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong>Payment Reminder:</strong> This unit has {formatCurrency(unit.transactionInfo.pendingAmount)} in pending maintenance fees for {unit.transactionInfo.monthsUnpaid} month{unit.transactionInfo.monthsUnpaid > 1 ? 's' : ''}.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Advance Amounts Section - Full Width */}
      <div className="row">
        <div className="col-12">
          <div className="card mb-4">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <i className="fas fa-hand-holding-usd me-2"></i>
                Advance Amounts
              </h6>
              <small className="text-muted">
                {advanceAmounts.length} record{advanceAmounts.length !== 1 ? 's' : ''}
              </small>
            </div>
            <div className="card-body">
              {advanceAmountsLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mt-2">Loading advance amounts...</p>
                </div>
              ) : advanceAmountsError ? (
                <div className="alert alert-warning">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {advanceAmountsError}
                </div>
              ) : advanceAmounts.length > 0 ? (
                <>
                  {/* Current Balance Summary */}
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <div className="text-center p-3 border rounded bg-light">
                        <div className="fw-bold text-info">Current Advance Balance</div>
                        <div className="fs-4 text-info fw-bold">
                          {formatCurrency(
                            advanceAmounts.reduce((total, amount) => {
                              return amount.advance_type === 'credit' 
                                ? total + parseFloat(amount.amount || 0)
                                : total - parseFloat(amount.amount || 0);
                            }, 0)
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="text-center p-3 border rounded bg-light">
                        <div className="fw-bold text-secondary">Total Records</div>
                        <div className="fs-4 text-secondary fw-bold">
                          {advanceAmounts.length}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Advance Amounts Table */}
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead className="table-dark">
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Remarks</th>
                          <th>Documents</th>
                        </tr>
                      </thead>
                      <tbody>
                        {advanceAmounts.slice(0, 15).map((amount) => {
                          // Parse document_url - handle both string and array formats
                          let documentUrls = [];
                          if (amount.document_url) {
                            if (Array.isArray(amount.document_url)) {
                              documentUrls = amount.document_url;
                            } else if (typeof amount.document_url === 'string') {
                              try {
                                const parsed = JSON.parse(amount.document_url);
                                documentUrls = Array.isArray(parsed) ? parsed : [parsed];
                              } catch (e) {
                                // Not JSON, treat as single URL
                                documentUrls = [amount.document_url];
                              }
                            }
                          }

                          return (
                            <tr key={amount.id}>
                              <td className="text-nowrap">{formatDateIST(amount.date)}</td>
                              <td>
                                <span className={`badge ${amount.advance_type === 'credit' ? 'bg-success' : 'bg-danger'}`}>
                                  {amount.advance_type === 'credit' ? 'Credit' : 'Debit'}
                                </span>
                              </td>
                              <td className={`fw-bold ${amount.advance_type === 'credit' ? 'text-success' : 'text-danger'}`}>
                                {amount.advance_type === 'credit' ? '+' : '-'}{formatCurrency(amount.amount || 0)}
                              </td>
                              <td>
                                <span className={`badge ${amount.status === 'active' ? 'bg-success' : amount.status === 'used' ? 'bg-secondary' : 'bg-warning text-dark'}`}>
                                  {amount.status ? amount.status.charAt(0).toUpperCase() + amount.status.slice(1) : 'Active'}
                                </span>
                              </td>
                              <td className="text-muted">
                                {amount.remarks || '-'}
                              </td>
                              <td>
                                {documentUrls.length > 0 ? (
                                  <div className="d-flex flex-wrap gap-1">
                                    {documentUrls.map((url, index) => (
                                      <a
                                        key={index}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-outline-primary btn-sm"
                                        title={`View Document ${index + 1}`}
                                      >
                                        <i className="fas fa-file-alt me-1"></i>
                                        Doc {index + 1}
                                      </a>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {advanceAmounts.length > 15 && (
                      <div className="text-center mt-2">
                        <small className="text-muted">
                          Showing recent 15 records out of {advanceAmounts.length} total
                        </small>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="fas fa-hand-holding-usd fa-2x mb-2 d-block"></i>
                  <p>No advance amounts found for this unit</p>
                  <small>Advance amount records will appear here once they are added.</small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnitDetail; 